import { useState } from "react";
import { Link } from "react-router-dom";
import SEO, { articleSchema, faqSchema } from "@/components/SEO";
import FAQSection, { GENERAL_FAQS } from "@/components/FAQSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  FileText, 
  ChevronRight, 
  ChevronLeft,
  Heart,
  Sparkles,
  Cake,
  Clock,
  BookOpen,
  GraduationCap
} from "lucide-react";

const BLOG_POSTS = [
  {
    id: "bryllupstale-guide",
    title: "Sådan skriver du den perfekte bryllupstale — 7 konkrete tips",
    description: "Skal du holde tale til et bryllup? Her får du en komplet guide med eksempler, struktur og de fejl du skal undgå.",
    category: "Bryllup",
    icon: Heart,
    color: "bg-rose-500",
    readTime: "8 min",
    heroImage: "/brand/moods/01-bryllup.png",
    content: `Bryllupstalen er en af de mest personlige og mindeværdige dele af hele bryllupsfesten. Uanset om du er forlover, bror, søster, ven eller forælder — her er 7 konkrete tips til at skrive en tale der rammer lige i hjertet.

## 1. Start med en god åbning — ikke "Kære alle sammen"

Den største fejl folk begår er at starte med "Kære alle sammen, tak fordi I er her i dag." Det er generisk og fanger ingen. Start i stedet med:

- **En personlig observation:** "Jeg har kendt Anna i 20 år, og i alle de år har hun haft én gennemgående egenskab..."
- **En sjov anekdote:** "Første gang Thomas fortalte mig om Anna, sagde han: 'Jeg tror hun er lidt irriteret på mig.' Det var hans måde at sige han var forelsket."
- **Et direkte henvendelse:** "Thomas — der er noget du skal vide."

## 2. Fortæl konkrete historier — ikke generelle komplimenter

"Anna er en fantastisk person" siger ingenting. Men "Anna er den type der møder op med suppe når du er syg — også selvom du bare har forkølelse" — det maler et billede. Brug dine anekdoter til at VISE hvem personen er, i stedet for at fortælle det.

**Gode anekdoter til en bryllupstale:**
- Hvordan parret mødtes (set fra dit perspektiv)
- Et sjovt minde du deler med brud/gom
- Et øjeblik hvor du tænkte "de to hører sammen"
- En personlighedstræk der viser hvem de er

## 3. Hold balancen mellem humor og varme

De bedste bryllupstaler veksler mellem sjove og rørende øjeblikke. Ren humor uden hjertevarme falder fladt, og ren sentiment bliver klægt. Start gerne let og sjovt, og byg op mod noget mere alvorligt.

**Vigtigt:** Gør aldrig grin MED brud eller gom. Gør grin med situationer, med dig selv, eller med sjove episoder.

## 4. Tal TIL parret — ikke OM dem

En klassisk fejl er at tale om parret i tredje person mens de sidder lige foran dig. Tal direkte til dem: "Anna og Thomas, I har..." i stedet for "Anna og Thomas er..."

Det gør talen mere intim og personlig.

## 5. Hold den korte nok

De fleste bryllupstaler er for lange. En god bryllupstale varer 3-5 minutter (ca. 500-800 ord). Hellere kort og rammende end lang og kedelig. Husk: Gæsterne har hørt 3-4 taler i løbet af aftenen.

## 6. Afslut med et klart signal

Gæsterne skal vide hvornår de skal rejse glasset. Afslut med:
- "Vil I rejse jeres glas for Anna og Thomas"
- "Skål for brudeparret!"
- Eller et callback til din åbningsanekdote efterfulgt af en skål

## 7. Øv dig — men lær den ikke udenad

Læs talen højt mindst 3 gange. Det afslører akavede formuleringer og giver dig en fornemmelse for timingen. Men lær den ikke udenad ord for ord — det lyder mekanisk. Hav den på papir eller telefon som sikkerhedsnet, men kig op og tal til parret.

---

**Vil du have hjælp?** MinFesttale skriver en personlig bryllupstale baseret på dine historier — klar på 2 minutter, fra 149 kr.`,
  },
  {
    id: "konfirmationstale-guide",
    title: "Konfirmationstale: Den komplette guide til forældre",
    description: "Skal du holde tale til din søn eller datters konfirmation? Her er alt du behøver at vide — fra struktur til følelser.",
    category: "Konfirmation",
    icon: Sparkles,
    color: "bg-amber-500",
    readTime: "7 min",
    heroImage: "/brand/moods/02-konfirmation.png",
    faqs: [
      {
        q: "Hvor lang skal en konfirmationstale være?",
        a: "2-4 minutter er perfekt, svarende til 300-600 ord. Kort nok til at holde opmærksomheden, lang nok til at sige noget meningsfuldt.",
      },
      {
        q: "Hvem holder tale til en konfirmation?",
        a: "Typisk starter konfirmanden selv, derefter forældre, søskende og nære familiemedlemmer. Mor og far kan også holde tale sammen.",
      },
      {
        q: "Hvad skal man sige i en konfirmationstale?",
        a: "Start med en barndomshistorie, fortæl om konfirmandins udvikling, beskriv hvem de er i dag, og afslut med et personligt budskab og en skål.",
      },
      {
        q: "Kan man holde en sjov konfirmationstale?",
        a: "Ja — de bedste konfirmationstaler blander humor med varme. Start gerne let og sjovt og byg op mod noget mere rørende.",
      },
      {
        q: "Hvad koster det at få skrevet en konfirmationstale?",
        a: "Hos MinFesttale koster en konfirmationstale fra 149 kr. og er klar på få minutter baseret på dine egne historier og minder.",
      },
    ],
    content: `Konfirmationstalen er et af de mest bevægende øjeblikke i en families liv. Dit barn står på tærsklen til at blive voksen, og du har 3-5 minutter til at sige noget de (og du) vil huske for evigt. Ingen pres.

## Hvem er talen til?

Det lyder banalt, men det vigtigste er at huske: Talen er til dit barn. Ikke til gæsterne. Ikke til bedsteforældrene. Til dit barn. Tal direkte til dem.

## Den perfekte struktur

### 1. Åbning — Fang opmærksomheden
Start med en konkret barndomshistorie. Noget sjovt eller rørende der viser hvem dit barn var som lille.

*Eksempel:* "Frederik, da du var 4 år, nægtede du at gå i seng uden at have sagt godnat til hver eneste bamse. Der var 23 af dem. Det tog en halv time."

### 2. Rejsen — Fra barn til ung voksen
Vælg 1-2 anekdoter der viser hvordan dit barn har udviklet sig. Hvad er de blevet gode til? Hvad er du stolt af? Vær specifik.

*Eksempel:* "Du husker sikkert ikke det selv, men din første skoledag stod du helt stille og kiggede på de andre børn. Så gik du hen til en dreng der stod alene og sagde: 'Skal vi lege?' Den dag vidste jeg at du ville klare dig."

### 3. Hvem er de i dag?
Beskriv de egenskaber du ser i dem nu. Brug eksempler — sig ikke bare "du er modig" men vis det med en historie.

### 4. Budskab — Hvad vil du sige?
Det her er kernen. Hvad vil du give dem med på vejen? Hold det kort og oprigtigt. Undgå klichéer som "verden ligger for dine fødder."

*Eksempel:* "Jeg vil sige dig én ting: Du behøver ikke have det hele fundet ud af. Heller ikke nu. Det har vi andre heller ikke. Men uanset hvad, så er vi her."

### 5. Afslutning
En kort, klar afslutning. Kan være en skål, et "tillykke", eller et callback til åbningshistorien.

## De 3 største fejl

1. **At tale over hovedet på dem.** Husk det er en teenager. Brug et sprog de forstår og kan relatere til.
2. **At gøre det for sentimentalt.** Konfirmanden vil gerne genkendes og føle sig set — ikke have det pinligt.
3. **At fortælle for mange historier.** Vælg 2-3 stærke anekdoter. Kvalitet over kvantitet.

## Hvor lang skal talen være?

2-4 minutter er perfekt. Det svarer til ca. 300-600 ord. Kort nok til at holde opmærksomheden, lang nok til at sige noget meningsfuldt.

---

**Sidder du fast?** MinFesttale genererer en personlig konfirmationstale baseret på dine anekdoter — klar på 2 minutter.`,
  },
  {
    id: "foedselsdagstale-tips",
    title: "Fødselsdagstale til rund fødselsdag — inspiration og gode råd",
    description: "Skal du holde tale til en 50, 60 eller 70 års fødselsdag? Her er den guide du har brug for.",
    category: "Fødselsdag",
    icon: Cake,
    color: "bg-emerald-500",
    readTime: "6 min",
    heroImage: "/brand/moods/03-rund-foedselsdag.png",
    faqs: [
      {
        q: "Hvad skal en tale til en rund fødselsdag indeholde?",
        a: "En god fødselsdagstale indeholder en sjov åbning, 2-3 historier der viser hvem fødselaren er, en hyldest, og en varm afslutning med skål.",
      },
      {
        q: "Hvor lang skal en tale til 50 eller 60 års fødselsdag være?",
        a: "3-5 minutter er ideelt. Gæsterne har hørt flere taler i løbet af aftenen, så hellere kort og præcis end lang og kedelig.",
      },
      {
        q: "Hvem holder tale til en rund fødselsdag?",
        a: "Typisk ægtefælle eller partner, børn, bedste venner og kolleger — alle der kender fødselaren godt og har gode historier at dele.",
      },
      {
        q: "Hvad er forskellen på en tale til 50, 60 og 70 års fødselsdag?",
        a: "Til 50 år kan tonen være lettere og mere humoristisk. Til 60 år balancerer man humor og dybde. Til 70 år er tonen mere højtidelig og reflekterende med fokus på taknemmelighed.",
      },
      {
        q: "Hvad koster det at få skrevet en tale til en rund fødselsdag?",
        a: "Hos MinFesttale koster en fødselsdagstale fra 149 kr. baseret på dine egne historier og minder om fødselaren.",
      },
    ],
    content: `Runde fødselsdage er noget særligt. 50, 60, 70 — det er milepæle der fortjener at blive fejret med en tale der rammer. Her er alt du behøver at vide.

## Hvem holder talen?

Runde fødselsdagstaler holdes typisk af:
- **Ægtefælle/partner** — den mest intime og personlige
- **Børn** — ofte sjov og kærlig, med barndomsminder
- **Bedste ven** — kan tillade sig mere humor
- **Kollega** — fokus på faglige og sociale sider

Uanset hvem du er, gælder de samme grundregler: Vær personlig, vær konkret, og hold den kort.

## Den gode struktur

### Åbning
Start med noget der fanger. En sjov observation, en anekdote, eller et statement.

*Eksempel (ægtefælle):* "Hanne, du sagde forleden at du 'slet ikke føler dig som 60.' Og det kan jeg bekræfte — du har stadig den samme energi som dengang vi mødtes. Bortset fra at du nu går i seng kl. 22."

### Anekdoter (2-3 styk)
Vælg historier der viser hvem fødselaren er. De bedste anekdoter er dem der får folk til at nikke genkendende: "Ja, det er SÅ typisk ham/hende."

### Hyldest
Hvad beundrer du? Hvad har personen bidraget med? Her er det okay at blive lidt rørende — det er en milepæl.

### Afslutning med skål
Saml talen op med et kort, varmt budskab og en skål.

## Tips baseret på alder

### 50 års fødselsdag
- Tonen kan være let og humoristisk
- Humor om alder er OK i små doser
- Fokus på de gode år og hvad der er opnået

### 60 års fødselsdag
- Balancer mellem humor og dybde
- Fremhæv personens essens — hvem de virkelig er
- Kan inkludere refleksion over livets faser

### 70 års fødselsdag
- Mere højtidelig og reflekterende tone
- Fokus på taknemmelighed og hyldest
- Historier fra mange årtier er guld værd

## Hvor lang?

3-5 minutter er ideelt (ca. 500-800 ord). Husk: De andre gæster vil også gerne fejre fødselaren.

## Den ene ting du skal huske

Den bedste fødselsdagstale er den der får fødselaren til at tænke: "Han/hun kender mig virkelig." Sørg for at dine anekdoter viser DET.

---

**Brug for hjælp?** MinFesttale skriver en personlig fødselsdagstale baseret på dine historier — fra 149 kr.`,
  },
  {
    id: "nervoesitet-tale-tips",
    title: "Nervøs for at holde tale? 5 tips der virker",
    description: "De fleste er nervøse for at holde tale. Her er 5 konkrete teknikker der gør det nemmere — også for dig.",
    category: "Tips",
    icon: BookOpen,
    color: "bg-violet-500",
    readTime: "5 min",
    heroImage: "/brand/moods/05-tale-i-haanden.png",
    content: `Lad os starte med sandheden: De fleste mennesker er nervøse for at holde tale. Undersøgelser viser at offentlig tale er en af de mest udbredte fobier overhovedet — foran edderkopper, højder og endda døden.

Så hvis du sidder med svedige håndflader ved tanken om at holde en tale til et bryllup, en konfirmation eller en fødselsdag — du er i godt selskab.

Her er 5 teknikker der faktisk virker:

## 1. Forberedelse slår talent

Den vigtigste faktor er ikke om du er en "god taler." Det er om du er forberedt. En gennemarbejdet tale med personlige historier vil ALTID slå en improviseret tale fra en naturlig performer.

**Hvad det betyder i praksis:**
- Skriv talen færdig mindst 3 dage inden
- Læs den højt mindst 5 gange
- Øv foran spejlet eller en du stoler på
- Tag tid på dig selv — du taler sandsynligvis hurtigere end du tror

## 2. De første 30 sekunder er alt

Nervøsiteten er værst de første 30 sekunder. Derefter falder pulsen, og du finder din rytme. Derfor er åbningen den vigtigste del at øve.

**Konkret tip:** Lær de første 2-3 sætninger udenad. Resten kan du have på papir. Men start stærkt og med selvtillid — det sætter tonen for hele talen.

## 3. Tal til ÉN person

Når du står foran 50 gæster, kan det føles overvældende. Tricket er at tale til én person ad gangen. Find et venligt ansigt, tal til dem i 5-10 sekunder, og flyt blikket til en anden.

Det giver øjenkontakt uden at du føler du "optræder."

## 4. Pauser er din ven

Nervøse talere taler for hurtigt og uden pauser. Men pauser er faktisk et af de mest kraftfulde virkemidler i en tale. De giver:

- Dig tid til at trække vejret
- Publikum tid til at absorbere det du har sagt
- Dine pointer mere vægt

**Regel:** Hold en kort pause efter hver vigtig sætning. Det føles som en evighed for dig — men for publikum føles det naturligt.

## 5. Det handler ikke om dig

Det vigtigste mindset-skift: Talen handler ikke om dig. Den handler om den person du taler til. Fokusér på at give dem en god oplevelse — på at dele dine følelser for dem — og din nervøsitet forsvinder.

Når du er oprigtigt optaget af at hyldest personen, glemmer du at være nervøs.

## Bonus: Det er OK at blive rørt

Mange er bange for at græde under talen. Men lad os være ærlige: Hvis du bliver rørt mens du taler om en person du elsker, er det ikke en svaghed. Det er det mest ægte og bevægende du kan gøre.

Hold en pause. Træk vejret. Fortsæt. Gæsterne vil elske dig for det.

---

**En god tale gør det nemmere.** Når du har en velskrevet tale foran dig, er det meget lettere at holde den. MinFesttale skriver en personlig tale baseret på dine historier — klar på 2 minutter.`,
  },
  {
    id: "studentertale-foraeldre-guide",
    title: "Studentertale fra forældre: Den komplette guide (2026)",
    description: "Skal du holde studentertale til dit barn? Her er strukturen, eksemplerne og de tre største fejl du skal undgå — så talen rammer hjertet.",
    category: "Studenter",
    icon: GraduationCap,
    color: "bg-sky-500",
    readTime: "7 min",
    heroImage: "/brand/moods/04-studentertale.png",
    faqs: [
      {
        q: "Hvad skal en studentertale fra forældre indeholde?",
        a: "En god studentertale indeholder en barndomshistorie, 2-3 anekdoter der viser studentens udvikling, et personligt budskab og en varm afslutning med skål.",
      },
      {
        q: "Hvor lang skal en studentertale være?",
        a: "3-5 minutter er ideelt, svarende til 400-700 ord. En præcis og personlig tale huskes bedre end en lang.",
      },
      {
        q: "Hvornår holder man tale til studenten?",
        a: "Typisk holder forældrene tale ved studenterfesten samme dag eller aftenen efter. Søskende og venner kan også holde tale.",
      },
      {
        q: "Hvad siger man i en studentertale?",
        a: "Tal direkte til studenten. Fortæl om hvem de var som barn, hvad de har kæmpet for, hvem de er blevet, og hvad du ønsker for dem fremover.",
      },
      {
        q: "Hvad koster det at få skrevet en studentertale?",
        a: "Hos MinFesttale koster en studentertale fra 149 kr. Du fortæller dine historier og minder — vi skriver talen klar til den store dag.",
      },
    ],
    content: `Din teenager har klaret det. Studentereksamen er i hus — og nu skal du stå foran familie og venner og sige noget der gør dem stolte, rørte og måske lidt griner.

Ingen pres.

Studentertalen fra forældre er en af de mest personlige taler du nogensinde kommer til at holde. Her er alt du behøver at vide for at gøre den uforglemmelig.

---

## Hvem er talen til?

Det lyder banalt, men det vigtigste at huske: Talen er til dit barn. Ikke til gæsterne. Ikke til bedsteforældrene. Til dit barn.

Tal direkte til dem. Se dem i øjnene. Lad dem mærke at du ser dem — ikke studenten, men mennesket.

---

## Den perfekte struktur

### 1. Åbning — Fang opmærksomheden

Start med en konkret barndomshistorie. Noget der viser hvem dit barn var — og peger frem mod hvem de er blevet.

**Eksempel:**
*"Sofie, da du var 7 år, besluttede du at du ville lære at ride på ethjulet cykel. Du faldt 47 gange — jeg talte — og stod op 48. Det vidste jeg dengang ville blive dit kendetegn."*

Undgå at starte med "Kære alle sammen" eller "Jeg er så stolt." Det ved alle allerede. Start i stedet med en historie.

### 2. Rejsen — Fra barn til student

Vælg 2-3 anekdoter der viser hvordan dit barn har udviklet sig gennem årene. Hvad husker du bedst? Hvad er du mest stolt af?

De bedste anekdoter er konkrete og specifikke — ikke "du arbejdede hårdt" men "du sad oppe til kl. 2 om natten med den der matematikopgave og nægtede at give op."

**Gode anekdoter til en studentertale:**
- En barndomshistorie der viser hvem de altid har været
- Et svært øjeblik de kom igennem
- Et øjeblik hvor du tænkte "mit barn er ved at blive voksen"
- En sjov episode fra gymnasietiden

### 3. Hvem er de i dag?

Beskriv de egenskaber du ser i dit barn nu. Brug eksempler — sig ikke bare "du er modig" men vis det med en konkret situation.

Dette er hjertets tale. Her er det okay at blive rørt.

### 4. Budskabet — Hvad vil du give dem med?

Det her er kernen i talen. Hvad vil du sige til dit barn på denne dag?

Hold det kort og oprigtigt. Undgå klichéer som "verden ligger for dine fødder" og "du kan alt hvad du vil." Sig i stedet noget der er sandt og specifikt for netop dit barn.

**Eksempel:**
*"Marcus, jeg vil sige dig én ting: Du behøver ikke have det hele fundet ud af. Heller ikke nu. Det har vi andre heller ikke. Men uanset hvad du vælger, så er vi her."*

### 5. Afslutning — Klar og varm

Afslut med en skål, et "tillykke" eller et callback til din åbningshistorie. Gæsterne skal vide hvornår de skal rejse glasset.

---

## De 3 største fejl i studentertaler

**1. For mange historier**
Vælg 2-3 stærke anekdoter. Kvalitet over kvantitet. En tale med 6 historier husker ingen — en tale med 2 perfekte historier husker alle.

**2. For sentimental fra start til slut**
De bedste taler veksler mellem let/sjovt og rørende. Start gerne med noget der får folk til at smile — og byg op mod det der virkelig betyder noget.

**3. At tale OM dem i stedet for TIL dem**
Tal direkte til dit barn. "Sofie, du er..." i stedet for "Sofie er..." Det gør talen intim og personlig på en måde der rammer hjertet.

---

## Hvor lang skal den være?

**3-5 minutter er perfekt.** Det svarer til cirka 400-700 ord.

Kortere end det og det føles overfladisk. Længere og du mister opmærksomheden — og det er der ingen der ønsker, hverken dig eller dit barn.

---

## Den ene ting du skal huske

Den bedste studentertale er den der får dit barn til at tænke: *"De kender mig virkelig."*

Ikke den der er flotteste. Ikke den der er sjovest. Den der er mest ægte.

---

## Sidder du fast?

MinFesttale skriver en personlig studentertale baseret på dine egne historier og minder om dit barn — klar på 2 minutter.

Du ser et preview med dine egne ord inden du betaler noget. Fra 149 kr.`,
  }
];

const BlogListPage = () => {
  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <SEO
        title="Blog — Guides og inspiration til festtaler | MinFesttale.dk"
        description="Gode råd til at skrive den perfekte bryllupstale, konfirmationstale, fødselsdagstale og mere. Konkrete tips, struktur og eksempler fra MinFesttale.dk."
        path="/blog"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-secondary font-medium text-sm uppercase tracking-wider">Blog</span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-primary mt-2">
            Guides og inspiration til din tale
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Gode råd, konkrete tips og eksempler til at skrive den perfekte festtale
          </p>
        </div>

        {/* Blog post cards */}
        <div className="space-y-6">
          {BLOG_POSTS.map((post) => (
            <Link key={post.id} to={`/blog/${post.id}`}>
              <Card 
                className="bg-white hover-lift border-border/40 overflow-hidden cursor-pointer transition-all hover:border-secondary/40"
                data-testid={`blog-card-${post.id}`}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row">
                    <div className={`sm:w-2 ${post.color} flex-shrink-0`} />
                    <div className="p-5 md:p-6 flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-full ${post.color} flex items-center justify-center flex-shrink-0`}>
                          <post.icon className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{post.category}</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <h2 className="font-heading text-lg md:text-xl font-bold text-primary mb-2 group-hover:text-secondary transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground text-sm md:text-base">
                        {post.description}
                      </p>
                      <span className="inline-flex items-center gap-1 text-secondary text-sm font-medium mt-3">
                        Læs mere <ChevronRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center bg-muted/30 rounded-xl p-8">
          <h3 className="font-heading text-xl md:text-2xl font-bold text-primary mb-3">
            Vil du hellere have hjælp?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Vores AI skriver en personlig tale baseret på dine historier — klar på 2 minutter.
          </p>
          <Link to="/tale">
            <Button className="bg-secondary hover:bg-secondary/90 text-primary rounded-full px-8 py-6 text-lg" data-testid="blog-cta">
              <FileText className="mr-2 h-5 w-5" />
              Start din tale nu
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const BlogPostPage = ({ postId }) => {
  const post = BLOG_POSTS.find(p => p.id === postId);
  
  if (!post) {
    return (
      <div className="min-h-screen bg-background py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-heading text-2xl font-bold text-primary">Indlæg ikke fundet</h1>
          <Link to="/blog">
            <Button className="mt-4" variant="outline">Tilbage til blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Simple markdown-to-JSX renderer
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      
      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-primary mt-8 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="font-heading text-lg font-bold text-primary mt-6 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('- **')) {
        const match = line.match(/^- \*\*(.+?)\*\*\s*(.*)$/);
        if (match) {
          elements.push(
            <li key={i} className="ml-6 text-muted-foreground mb-2 list-disc">
              <strong className="text-primary">{match[1]}</strong> {match[2]}
            </li>
          );
        }
      } else if (line.startsWith('- ')) {
        elements.push(
          <li key={i} className="ml-6 text-muted-foreground mb-2 list-disc">
            {line.replace('- ', '')}
          </li>
        );
      } else if (line.startsWith('*Eksempel')) {
        const cleanLine = line.replace(/^\*/, '').replace(/\*$/, '');
        elements.push(
          <blockquote key={i} className="border-l-4 border-secondary pl-4 py-2 my-4 bg-secondary/5 rounded-r-lg">
            <p className="text-muted-foreground italic text-sm md:text-base">{cleanLine}</p>
          </blockquote>
        );
      } else if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <p key={i} className="font-medium text-primary my-3">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      } else if (line.startsWith('**')) {
        const cleanLine = line.replace(/\*\*/g, '');
        elements.push(
          <p key={i} className="font-medium text-primary my-3">{cleanLine}</p>
        );
      } else if (line === '---') {
        elements.push(<hr key={i} className="my-8 border-border/40" />);
      } else if (line.trim() === '') {
        // skip empty lines
      } else {
        elements.push(
          <p key={i} className="text-muted-foreground leading-relaxed mb-4">
            {line}
          </p>
        );
      }
      i++;
    }
    return elements;
  };

  // Build FAQ list: topic-specific FAQs (if any) followed by general FAQs.
  // De-duplicate by question text in case any specific question overlaps with general ones.
  const combinedFaqs = (() => {
    const list = [...(post.faqs || []), ...GENERAL_FAQS];
    const seen = new Set();
    return list.filter(({ q }) => {
      const key = q.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <SEO
        title={`${post.title} | MinFesttale.dk`}
        description={post.description}
        path={`/blog/${post.id}`}
        type="article"
        image={post.heroImage}
        schema={[
          articleSchema({
            title: post.title,
            description: post.description,
            path: `/blog/${post.id}`,
            category: post.category,
            image: post.heroImage,
          }),
          faqSchema(combinedFaqs),
        ]}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Breadcrumb */}
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors" data-testid="blog-back-link">
          <ChevronLeft className="h-4 w-4" />
          Tilbage til blog
        </Link>

        {/* Hero image */}
        {post.heroImage && (
          <div className="rounded-2xl overflow-hidden mb-8 shadow-lg" data-testid="blog-hero-image">
            <img
              src={post.heroImage}
              alt={post.title}
              className="w-full h-auto aspect-[16/9] object-cover"
            />
          </div>
        )}

        {/* Post header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-8 h-8 rounded-full ${post.color} flex items-center justify-center`}>
              <post.icon className="h-4 w-4 text-white" />
            </div>
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{post.category}</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.readTime} læsetid
            </span>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-primary leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>
        </div>

        {/* Post content */}
        <article className="prose-custom" data-testid="blog-post-content">
          {renderContent(post.content)}
        </article>

        {/* FAQ section (topic-specific + general) — also emits FAQPage JSON-LD via SEO */}
        <FAQSection
          faqs={combinedFaqs}
          title="Ofte stillede spørgsmål"
          subtitle={post.faqs ? `Om ${post.category.toLowerCase()}staler og MinFesttale` : "Om MinFesttale"}
          testId="blog-faq"
        />

        {/* CTA after article */}
        <Card className="mt-12 border-2 border-secondary bg-secondary/5" data-testid="blog-post-cta">
          <CardContent className="p-6 md:p-8 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-primary mb-3">
              Klar til at skrive din tale?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Udfyld en kort formular med dine personlige historier — vores AI skriver en tale du kan være stolt af. Fra 149 kr.
            </p>
            <Link to="/tale">
              <Button className="bg-secondary hover:bg-secondary/90 text-primary rounded-full px-8 py-6 text-lg">
                <FileText className="mr-2 h-5 w-5" />
                Start din tale nu
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Other posts */}
        <div className="mt-12">
          <h3 className="font-heading text-lg font-bold text-primary mb-4">Læs også</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {BLOG_POSTS.filter(p => p.id !== post.id).slice(0, 2).map((otherPost) => (
              <Link key={otherPost.id} to={`/blog/${otherPost.id}`}>
                <Card className="bg-white hover-lift border-border/40 cursor-pointer h-full" data-testid={`related-post-${otherPost.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-6 h-6 rounded-full ${otherPost.color} flex items-center justify-center`}>
                        <otherPost.icon className="h-3 w-3 text-white" />
                      </div>
                      <span className="text-xs text-muted-foreground">{otherPost.category}</span>
                    </div>
                    <h4 className="font-heading text-sm font-bold text-primary">
                      {otherPost.title}
                    </h4>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export { BLOG_POSTS, BlogListPage, BlogPostPage };
export default BlogListPage;
